interface Payment {
  month: string
  year?: string
  total_amount: string
}
interface AmoutRequested {
  month: string
  total_refunded_amount: string
}
interface AmountRequestedByBailiffs {
  month: string
  total_bailiff_refunded_amount: string
}
interface BiddersDownloadedRefunded {
  month: string
  total_bidders_refunded_amount: string
}
interface BailiffDownloadedForRefund {
  month: string
  total_bailiff_downloaded_refunded: string
}

interface Document {
  month: string
  year?: string
  total_amount: string
}

interface Data {
  payments: Payment[]
  documents: Document[]
  amountRequested: AmoutRequested[]
  amountRequestedByBailiffs: AmountRequestedByBailiffs[]
  biddersDownloadedRefunded: BiddersDownloadedRefunded[]
  bailiffDownloadedForRefund: BailiffDownloadedForRefund[]
}

interface BG {
  month: string
  total_security_amount: string
}

interface CC {
  month: string
  total_security_amount: string
}

interface DataInstitution {
  bid_guarantees: BG[]
  certified_checks: CC[]
}

// export function generateChartRefundDataMFinance(
//   barData: Array<{ month: string, amountRequested?: string, amountRefunded?: string }> = []
// ): Array<Array<string | number>> {
//   const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
//   const chartData: Array<Array<string | number>> = [['Months', 'Amount Requested', 'Amount Refunded']]
//
//   let hasData = false
//
//   for (let i = 0; i < months.length; i++) {
//     const monthLabel = months[i]
//     const monthIndex = (i + 1).toString()
//
//     const entry = barData.find((e) => e?.month === monthIndex)
//
//     const requested = ((entry?.amountRequested) != null) ? parseInt(entry.amountRequested, 10) : 0
//     const refunded = ((entry?.amountRefunded) != null) ? parseInt(entry.amountRefunded, 10) : 0
//
//     if (requested !== 0 || refunded !== 0) hasData = true
//
//     chartData.push([monthLabel, requested, refunded])
//   }
//
//   // If there's no data, add a dummy row to avoid chart error
//   if (!hasData) {
//     chartData.push(['N/A', 0, 0])
//   }
//
//   return chartData
// }

export function generateChartRefundDataMFinance(
  barData: Array<{
    year: string
    months: Array<{ month: string, amountRequested?: string, amountRefunded?: string }>
  }> = []
): Array<Array<string | number>> {
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
  const chartData: Array<Array<string | number>> = [['Month', 'Amount Requested', 'Amount Refunded']]

  let hasData = false

  barData.forEach((yearItem) => {
    // Create a map of month number -> data for quick lookup
    const monthMap = new Map<string, { amountRequested?: string, amountRefunded?: string }>()
    yearItem.months.forEach((m) => {
      monthMap.set(m.month, m)
    })

    // Loop over all 12 months to ensure no missing
    months.forEach((monthLabel, index) => {
      const monthNum = (index + 1).toString()
      const m = monthMap.get(monthNum)

      const requested = ((m?.amountRequested) != null) ? parseInt(m.amountRequested, 10) : 0
      const refunded = ((m?.amountRefunded) != null) ? parseInt(m.amountRefunded, 10) : 0

      if (requested !== 0 || refunded !== 0) hasData = true

      chartData.push([`${monthLabel} ${yearItem.year}`, requested, refunded])
    })
  })

  // Avoid chart errors when no data
  if (!hasData) {
    chartData.push(['N/A', 0, 0])
  }

  return chartData
}

export type FinanceRefundChartMatrix = Array<Array<string | number>>

/**
 * Single-series line/bar data from the 3-column refund matrix
 * `['Month', 'Amount Requested', 'Amount Refunded']`.
 */
export function buildFinanceKpiSingleSeriesTrend(
  chartRefundMatrix: FinanceRefundChartMatrix | undefined | null,
  column: 1 | 2,
  seriesLabel: string
): FinanceRefundChartMatrix {
  const empty: FinanceRefundChartMatrix = [['Month', seriesLabel], ['N/A', 0]]
  if (chartRefundMatrix == null || !Array.isArray(chartRefundMatrix) || chartRefundMatrix.length < 2) {
    return empty
  }
  const header = chartRefundMatrix[0]
  if (!Array.isArray(header) || header.length < 3) return empty
  const out: FinanceRefundChartMatrix = [['Month', seriesLabel]]
  for (let i = 1; i < chartRefundMatrix.length; i++) {
    const row = chartRefundMatrix[i]
    if (!Array.isArray(row) || row.length < 3) continue
    const label = String(row[0])
    const raw = row[column]
    const v = typeof raw === 'number' ? raw : parseInt(String(raw ?? '0'), 10)
    const n = Number.isFinite(v) ? v : 0
    out.push([label, n])
  }
  if (out.length < 2) return empty
  const dataRows = out.slice(1)
  const hasNonPlaceholder = dataRows.some(
    (r) => r[0] !== 'N/A' && Number(r[1]) !== 0
  )
  if (!hasNonPlaceholder) return empty
  return out
}

/**
 * Two-series trend for pipeline context: requested volume vs completed refunds per month.
 */
export function buildFinanceKpiPipelineCompareTrend(
  chartRefundMatrix: FinanceRefundChartMatrix | undefined | null
): FinanceRefundChartMatrix {
  const empty: FinanceRefundChartMatrix = [
    ['Month', 'Amount requested (RWF)', 'Amount refunded (completed) (RWF)'],
    ['N/A', 0, 0]
  ]
  if (chartRefundMatrix == null || !Array.isArray(chartRefundMatrix) || chartRefundMatrix.length < 2) {
    return empty
  }
  const header = chartRefundMatrix[0]
  if (!Array.isArray(header) || header.length < 3) return empty
  const out: FinanceRefundChartMatrix = [
    ['Month', 'Amount requested (RWF)', 'Amount refunded (completed) (RWF)']
  ]
  for (let i = 1; i < chartRefundMatrix.length; i++) {
    const row = chartRefundMatrix[i]
    if (!Array.isArray(row) || row.length < 3) continue
    const r1 = typeof row[1] === 'number' ? row[1] : parseInt(String(row[1] ?? '0'), 10)
    const r2 = typeof row[2] === 'number' ? row[2] : parseInt(String(row[2] ?? '0'), 10)
    out.push([
      String(row[0]),
      Number.isFinite(r1) ? r1 : 0,
      Number.isFinite(r2) ? r2 : 0
    ])
  }
  if (out.length < 2) return empty
  const hasReal = out.slice(1).some(
    (r) =>
      r[0] !== 'N/A' &&
      (Number(r[1]) !== 0 || Number(r[2]) !== 0)
  )
  if (!hasReal) return empty
  return out
}

/** Same barData shape as mFinanceRefund; expands each year to JAN–DEC for Google Charts. */
export function generateChartBidSecurityDataMFinance(
  barData: Array<{
    year: string
    months: Array<{
      month: string
      bidGuarantee?: string
      certifiedChecks?: string
      payments?: string
    }>
  }> = []
): Array<Array<string | number>> {
  const months = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC'
  ]
  const chartData: Array<Array<string | number>> = [
    ['Month', 'Bid Guarantee', 'Certified checks', 'Payments']
  ]

  let hasData = false

  barData.forEach((yearItem) => {
    const monthMap = new Map<
    string,
    { bidGuarantee?: string, certifiedChecks?: string, payments?: string }
    >()
    yearItem.months.forEach((m) => {
      monthMap.set(m.month, m)
    })

    months.forEach((monthLabel, index) => {
      const monthNum = (index + 1).toString()
      const m = monthMap.get(monthNum)

      const bidGuarantee =
        m?.bidGuarantee != null ? parseInt(m.bidGuarantee, 10) : 0
      const certifiedChecks =
        m?.certifiedChecks != null ? parseInt(m.certifiedChecks, 10) : 0
      const payments = m?.payments != null ? parseInt(m.payments, 10) : 0

      if (bidGuarantee !== 0 || certifiedChecks !== 0 || payments !== 0) {
        hasData = true
      }

      chartData.push([
        `${monthLabel} ${yearItem.year}`,
        bidGuarantee,
        certifiedChecks,
        payments
      ])
    })
  })

  if (!hasData) {
    chartData.push(['N/A', 0, 0, 0])
  }

  return chartData
}

export type MFinanceMonthlySeriesKey = 'payments' | 'documents'

/**
 * Builds a Google Charts matrix from `/statistics/mfinance` `barData`.
 * Use `documents` for bid-security amounts (BG/CC via `bidders_bids`); `payments` for the `payments` table monthly totals.
 * DB drivers may return `month` as a number — we compare using string month keys.
 */
function generateChartDataMFinance(
  data: Pick<Data, 'payments' | 'documents'> | null | undefined,
  series: MFinanceMonthlySeriesKey = 'payments'
): Array<Array<string | number>> {
  const months: string[] = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC'
  ]

  const currentYear = new Date().getFullYear().toString()
  const rows = data?.[series]

  // Empty series: still return a valid matrix (GoogleChartOrEmpty needs ≥2 rows)
  if (rows == null || !Array.isArray(rows) || rows.length === 0) {
    const header = ['Months', `${currentYear} Amount`]
    const barData: Array<Array<string | number>> = [header]
    for (let i = 0; i < months.length; i++) {
      barData.push([months[i], 0])
    }
    return barData
  }

  // Get all unique years from the data, with fallback to current year
  const yearSet = new Set(rows.map((p) => String(p.year ?? currentYear)))
  const years = Array.from(yearSet).sort((a, b) => a.localeCompare(b))

  // If no years found, use current year
  if (years.length === 0) {
    years.push(currentYear)
  }

  // Create header row with years
  const header = ['Months', ...years.map(year => `${String(year)} Amount`)]
  const barData: Array<Array<string | number>> = [header]

  // Process each month
  for (let i = 0; i < months.length; i++) {
    const month = months[i]
    const monthIndex = (i + 1).toString()
    const row: Array<string | number> = [month]

    // For each year, find the amount for this month
    for (const year of years) {
      const payment = rows.find((p) =>
        String(p.month) === monthIndex &&
        (String(p.year ?? '') === year || ((p.year == null || p.year === '') && year === currentYear))
      )
      const raw = payment?.total_amount
      const paymentAmount =
        raw != null && raw !== '' ? parseInt(String(raw), 10) : 0
      row.push(Number.isFinite(paymentAmount) ? paymentAmount : 0)
    }

    barData.push(row)
  }

  return barData
}

function generatePieChartInstitution(bg: number, cc: number): Array<Array<string | number>> {
  return [
    ['Bid Security', 'Hours per Day'],
    ['Bid guarantee', bg],
    ['Certified checks', cc]
  ]
}

function generateChartDataInstitution(data: DataInstitution): Array<Array<string | number>> {
  const months: string[] = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC'
  ]
  const barData: Array<Array<string | number>> = [['Months', 'Bid Guarantee', 'Certified checks']]

  for (let i = 0; i < months.length; i++) {
    const month = months[i]
    const bidGuarantees = data.bid_guarantees.find((p) => p.month === (i + 1).toString())
    const certifiedChecks = data.certified_checks.find((d) => d.month === (i + 1).toString())
    const paymentAmount =
      bidGuarantees != null ? parseInt(bidGuarantees.total_security_amount, 10) : 0
    const documentAmount =
      certifiedChecks != null ? parseInt(certifiedChecks.total_security_amount, 10) : 0

    barData.push([month, paymentAmount, documentAmount])
  }

  return barData
}

export {
  generateChartDataMFinance,
  generatePieChartInstitution,
  generateChartDataInstitution
}
